import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { Box, Stack, Typography, Menu, MenuItem, IconButton, Button, Grid, Tooltip } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useState, type FC, type ReactNode } from "react";
import {
  type GetPartnerByIdResponse,
  addPartnerRoleMutationOptions,
  deletePartnerRoleMutationOptions,
  PARTNER_ROLES,
  type PartnerRole,
  type RoleConnectedEntitiesFormType,
} from "../../api/partners";
import { DynamicForm } from "../../dynamicForm";
import { DynamicShared } from "../../dynamicShared";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { ValidityText } from "../../agreements/AgreementDate";
import { isAgreementExpired } from "../../agreements/isAgreementExpired";
import { FileTextIcon, TrashCanIcon } from "../../shared/icons";
import { ROLES_INFO } from "./constants";
import { AgreementUsageNote, getSystemIdsWithAgreementSelected } from "./AgreementUsageNote";
import { CONNECTED_ENTITIES_FORM_TYPES_INFO } from "./PartnerRoleSubDrawerConnectedEntities";
import type { RoleActiveStatus } from "../../api/partners";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import type { Agreement } from "../../api/partners";
import { WhiteSpace } from "@privacy/ui";
import { multilineEllipsis } from "../../shared/multilineEllipsis";
import { useMutation } from "@tanstack/react-query";

type DeleteRoleDialogState = { role: PartnerRole; isOpen: true } | { role?: undefined; isOpen: false };

export const PartnerDrawerRolesSection = ({ partner }: { partner: GetPartnerByIdResponse }) => {
  const { t } = useTranslation();
  const addRoleMutation = useMutation(addPartnerRoleMutationOptions({ partnerId: partner.id }));
  const deleteRoleMutation = useMutation(deletePartnerRoleMutationOptions({ partnerId: partner.id }));
  const [deleteRoleDialogState, setDeleteRoleDialogState] = useState<DeleteRoleDialogState>({ isOpen: false });
  const existingRoles = partner.rolesAndRelationships.map(r => r.role);
  const availableRoles = PARTNER_ROLES.filter(role => !existingRoles.includes(role));

  const sortedRolesByActiveStatus = [...partner.rolesAndRelationships].sort((a, z) => {
    if (a.activeStatus === "active" && z.activeStatus !== "active") return -1;
    if (a.activeStatus !== "active" && z.activeStatus === "active") return 1;
    return 0;
  });

  const roleToDelete = partner.rolesAndRelationships.find(role => role.role === deleteRoleDialogState.role);
  const systemsUsingRoleAgreementsCount = getSystemIdsWithAgreementSelected(
    roleToDelete?.agreementDocuments ?? []
  ).size;

  return (
    <DynamicForm.InlineFormSection title={t("Partner.Roles.SectionTitle")}>
      <Stack sx={{ gap: 1, mt: 1 }}>
        {sortedRolesByActiveStatus.length === 0 && !addRoleMutation.isPending && <RoleCardEmptyState />}

        {sortedRolesByActiveStatus.length > 0 && (
          <Stack sx={{ gap: 1 }}>
            {sortedRolesByActiveStatus.map(roleData => (
              <RoleCard
                key={roleData.role}
                roleData={roleData}
                partner={partner}
                onDelete={() => setDeleteRoleDialogState({ role: roleData.role, isOpen: true })}
              />
            ))}
          </Stack>
        )}

        {addRoleMutation.isError && <DynamicForm.ErrorMessage />}

        {addRoleMutation.isPending && !existingRoles.includes(addRoleMutation.variables.role) && (
          <Box
            sx={{
              "width": "100%",
              "opacity": 0,
              "transform": "translate3d(0, -4px, 0)",
              "willChange": "opacity, transform",
              "animation": "roleCardFadeIn 800ms cubic-bezier(0.19, 1, 0.22, 1) forwards",
              "@keyframes roleCardFadeIn": {
                from: { opacity: 0, transform: "translate3d(0, -4px, 0)" },
                to: { opacity: 1, transform: "translate3d(0, 0, 0)" },
              },
            }}
          >
            <RoleCardLayout role={addRoleMutation.variables.role} activeStatus="inactive" disabled>
              <RoleCardTopRow
                role={addRoleMutation.variables.role}
                activeStatus="inactive"
                partnerName={partner.name}
                onDelete={() => setDeleteRoleDialogState({ role: addRoleMutation.variables.role, isOpen: true })}
              />
              <RoleCardBottomRow agreements={[]} />
            </RoleCardLayout>
          </Box>
        )}

        {availableRoles.length > 0 && (
          <AddRoleButton availableRoles={availableRoles} onAddRole={role => addRoleMutation.mutateAsync({ role })} />
        )}
      </Stack>
      {deleteRoleDialogState.isOpen && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setDeleteRoleDialogState({ isOpen: false })}
          onDelete={() => deleteRoleMutation.mutateAsync(deleteRoleDialogState.role)}
          headerText={t("Partner.Roles.Delete.ConfirmHeader")}
          message={
            <>
              <Translate
                translationKey="Partner.Roles.Delete.ConfirmMessage"
                values={{
                  role: (
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {t(ROLES_INFO[deleteRoleDialogState.role].labelKey)}
                    </Box>
                  ),
                }}
              />
              <AgreementUsageNote
                count={systemsUsingRoleAgreementsCount}
                oneKey="Partner.Roles.Delete.SelectedOnSystems.One"
                manyKey="Partner.Roles.Delete.SelectedOnSystems.Many"
              />
            </>
          }
          confirmButtonLabel={t("Partner.Roles.Delete.ConfirmButton")}
        />
      )}
    </DynamicForm.InlineFormSection>
  );
};

const DELETE_ROLE_BUTTON_CLASS_NAME = "delete-role-button";

type RoleCardProps = {
  partner: GetPartnerByIdResponse;
  roleData: GetPartnerByIdResponse["rolesAndRelationships"][number];
  onDelete: () => void;
};

const RoleCard: FC<RoleCardProps> = ({ partner, roleData, onDelete }) => {
  const { navigate } = usePrivacyNavigate();

  return (
    <Box>
      <RoleCardLayout role={roleData.role} onClick={() => navigate(roleData.role)} activeStatus={roleData.activeStatus}>
        <RoleCardTopRow
          onDelete={onDelete}
          partnerName={partner.name}
          role={roleData.role}
          activeStatus={roleData.activeStatus}
          description={roleData.description}
          formType={roleData.formType}
          connectedEntitiesInfo={
            roleData.activeStatus === "active" ? (
              <Typography
                sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
              >
                <Translate
                  translationKey={`${CONNECTED_ENTITIES_FORM_TYPES_INFO[roleData.formType].roleCardLabelKey}${roleData.connectedEntities.length === 1 ? "" : "Plural"}`}
                  values={{
                    count: (
                      <Box component="span" sx={{ fontWeight: "600" }}>
                        {roleData.totalConnectedEntitiesCount}
                      </Box>
                    ),
                  }}
                />
              </Typography>
            ) : null
          }
        />

        <RoleCardBottomRow agreements={roleData.agreementDocuments} />
      </RoleCardLayout>
    </Box>
  );
};

const RoleCardLayout: FC<{
  children: ReactNode;
  role: PartnerRole;
  onClick?: () => void;
  activeStatus: RoleActiveStatus;
  disabled?: boolean;
}> = ({ children, onClick, activeStatus, disabled }) => {
  return (
    <Box
      component="button"
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={theme => ({
        "all": "unset",
        "border": "1px solid",
        "borderColor": theme.palette.grey["300"],
        "borderRadius": 1,
        "width": "100%",
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
        [`& .${DELETE_ROLE_BUTTON_CLASS_NAME}`]: { visibility: "hidden" },
        [`&:is(:hover, :focus-visible, :focus-within) .${DELETE_ROLE_BUTTON_CLASS_NAME}`]: {
          visibility: activeStatus === "inactive" ? "visible" : "hidden",
        },
        [`&:hover:not(:has(.${DELETE_ROLE_BUTTON_CLASS_NAME}:is(:hover, :focus-visible)))`]: {
          backgroundColor: theme.palette.action.hover,
          cursor: "pointer",
        },
      })}
    >
      {children}
    </Box>
  );
};

const RoleCardTopRow: FC<{
  role: PartnerRole;
  activeStatus: RoleActiveStatus;
  description?: string | null;
  connectedEntitiesInfo?: ReactNode;
  formType?: RoleConnectedEntitiesFormType;
  partnerName: string;
  onDelete: () => void;
}> = ({ role, activeStatus, description, connectedEntitiesInfo, formType, partnerName, onDelete }) => {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ pt: 2, px: 2, pb: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gridTemplateRows: "auto auto",
            columnGap: 2,
            rowGap: 1.5,
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", minWidth: 0 }}>
            {ROLES_INFO[role].renderIcon({ size: "md", activeStatus })}
            <Typography
              component="h3"
              sx={theme => ({
                lineHeight: 1,
                color: theme.palette.text.primary,
                fontSize: theme.typography.pxToRem(14),
                fontWeight: 600,
              })}
            >
              {t(ROLES_INFO[role].labelKey)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}>
            <DeleteRoleButton onClick={onDelete} />
            <RoleCardStatusBadge activeStatus={activeStatus} role={role} formType={formType} />
          </Box>
          <Typography
            sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
          >
            {typeof description === "string" ? description : t(ROLES_INFO[role].descriptionKey, { partnerName })}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mr: 1 }}>{connectedEntitiesInfo}</Box>
        </Box>
      </Box>
    </>
  );
};

const RoleCardBottomRow: FC<{ agreements: Agreement[] }> = ({ agreements }) => {
  return (
    <Stack sx={{ p: 2, pt: 1.5, gap: 1 }}>
      <AgreementsTable agreements={agreements} />
    </Stack>
  );
};

const PADDING_X = 2;

const AgreementsTable: FC<{ agreements: Agreement[] }> = ({ agreements }) => {
  const { t, formatDate } = useTranslation();

  return (
    <Stack sx={{ gap: 1 }}>
      <Box
        sx={{
          borderRight: 1,
          borderRadius: GRID_BORDER_RADIUS,
          border: 1,
          borderColor: theme => theme.palette.divider,
        }}
      >
        <Grid
          sx={{
            mixBlendMode: "multiply",
            backgroundColor: theme => theme.palette.grey["100"],
            alignItems: "center",
            px: PADDING_X,
          }}
          spacing={COLUMN_SPACING}
          container
        >
          <Grid size={COLUMN_WIDTHS.title}>
            <AgreementHeaderCell>{t("Partner.Agreement.Grid.TitleHeader")}</AgreementHeaderCell>
          </Grid>
          <Grid size={COLUMN_WIDTHS.validUntil}>
            <AgreementHeaderCell>{t("Partner.Agreement.Grid.ValidUntilHeader")}</AgreementHeaderCell>
          </Grid>
        </Grid>

        {agreements.length === 0 ? (
          <AgreementTableRow
            title={
              <Typography
                sx={{ fontWeight: "400", fontSize: "inherit", color: (theme: Theme) => theme.palette.text.tertiary }}
              >
                {t("Partner.Roles.Status.NoAgreement")}
              </Typography>
            }
            validUntil={
              <Typography
                sx={{ fontWeight: "400", fontSize: "inherit", color: (theme: Theme) => theme.palette.text.tertiary }}
              >
                <WhiteSpace />-
              </Typography>
            }
          />
        ) : (
          agreements.map(agreement => {
            return (
              <AgreementTableRow
                key={agreement.id}
                title={
                  <DynamicShared.ExternalLink Icon={FileTextIcon} href={agreement.url}>
                    <Box component="span" sx={{ ...multilineEllipsis(1) }}>
                      {agreement.title}
                    </Box>
                  </DynamicShared.ExternalLink>
                }
                validUntil={
                  agreement.validUntil ? (
                    <ValidityText expired={isAgreementExpired(agreement)}>
                      {isAgreementExpired(agreement)
                        ? t("System.SuppliersAgreements.Agreement.Expired", { date: formatDate(agreement.validUntil) })
                        : formatDate(agreement.validUntil)}
                    </ValidityText>
                  ) : (
                    "-"
                  )
                }
              />
            );
          })
        )}
      </Box>
    </Stack>
  );
};

const AgreementTableRow: FC<{
  title: ReactNode;
  validUntil: ReactNode;
}> = ({ title, validUntil }) => {
  return (
    <Grid
      container
      spacing={COLUMN_SPACING}
      sx={{ px: PADDING_X, borderTop: 1, borderColor: "divider", alignItems: "center", py: 1.5 }}
    >
      <Grid size={COLUMN_WIDTHS.title}>
        <AgreementCell>{title}</AgreementCell>
      </Grid>
      <Grid size={COLUMN_WIDTHS.validUntil}>
        <AgreementCell>{validUntil}</AgreementCell>
      </Grid>
    </Grid>
  );
};

const COLUMN_WIDTHS = { title: 8, validUntil: 4 } as const;
const GRID_BORDER_RADIUS = 0.5;
const COLUMN_SPACING = 2;

const AgreementHeaderCell = ({ children }: { children: ReactNode }) => {
  return (
    <Typography
      sx={{
        py: 0.5,
        fontSize: theme => theme.typography.pxToRem(12),
        fontWeight: "medium",
        color: theme => theme.palette.text.primary,
      }}
    >
      {children}
    </Typography>
  );
};

const AgreementCell = ({ children }: { children: ReactNode }) => {
  return (
    <Box
      sx={{
        fontSize: theme => theme.typography.pxToRem(12),
        wordBreak: "break-word",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontWeight: "400",
      }}
    >
      {children}
    </Box>
  );
};

export const RoleCardStatusBadge: FC<{
  activeStatus: RoleActiveStatus;
  role: PartnerRole;
  formType?: RoleConnectedEntitiesFormType;
}> = ({ activeStatus, role, formType }) => {
  const { t } = useTranslation();
  const { labelKey, borderColor, textColor, dotColor } = ACTIVE_STATUS_INFO[activeStatus];

  const tooltipKey = formType ? TOOLTIP_KEYS[formType][activeStatus] : undefined;

  const badgeContent = (
    <Stack
      sx={{
        border: "1px solid",
        borderColor,
        borderRadius: 3,
        display: "flex",
        flexDirection: "row",
        px: 2,
        py: 0.5,
        gap: 1,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ flexShrink: 0, bgcolor: dotColor, height: "9px", width: "9px", borderRadius: "50%" }} />
      <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(12), color: textColor }}>
        {t(labelKey)}
      </Typography>
    </Stack>
  );

  if (tooltipKey) {
    return (
      <Tooltip
        followCursor
        title={
          <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), textWrap: "pretty" }}>
            <Translate
              translationKey={tooltipKey}
              values={{
                role: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {t(ROLES_INFO[role].labelKey)}
                  </Box>
                ),
              }}
            />
          </Typography>
        }
        slotProps={{
          tooltip: {
            sx: {
              margin: 3,
              backgroundColor: theme => theme.palette.background.paper,
              color: theme => theme.palette.text.primary,
              px: 2,
              py: 1.5,
              boxShadow: theme => theme.shadows[1],
              fontSize: theme => theme.typography.pxToRem(12),
            },
          },
        }}
      >
        {badgeContent}
      </Tooltip>
    );
  }

  return badgeContent;
};

const TOOLTIP_KEYS: Record<RoleConnectedEntitiesFormType, Record<RoleActiveStatus, TranslationKey>> = {
  privacyRegistration: {
    active: "Partner.Roles.Status.Active.PrivacyRegistration.Tooltip",
    inactive: "Partner.Roles.Status.Inactive.PrivacyRegistration.Tooltip",
  },
  privacyProcessorRegistration: {
    active: "Partner.Roles.Status.Active.PrivacyProcessorRegistration.Tooltip",
    inactive: "Partner.Roles.Status.Inactive.PrivacyProcessorRegistration.Tooltip",
  },
  system: {
    active: "Partner.Roles.Status.Active.System.Tooltip",
    inactive: "Partner.Roles.Status.Inactive.System.Tooltip",
  },
};

const ACTIVE_STATUS_INFO: Record<
  RoleActiveStatus,
  {
    borderColor: (theme: Theme) => string;
    textColor: (theme: Theme) => string;
    dotColor: (theme: Theme) => string;
    labelKey: TranslationKey;
  }
> = {
  active: {
    borderColor: theme => theme.palette.grey["500"],
    textColor: theme => theme.palette.text.primary,
    dotColor: theme => theme.palette.brandGreen["500"],
    labelKey: "Partner.Roles.Active",
  },
  inactive: {
    borderColor: theme => theme.palette.grey["500"],
    textColor: theme => theme.palette.text.primary,
    dotColor: theme => theme.palette.text.disabled,
    labelKey: "Partner.Roles.Inactive",
  },
};

const DeleteRoleButton: FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <IconButton
      aria-label={t("Partner.Roles.Delete.Tooltip")}
      onClick={e => {
        e.stopPropagation();
        onClick?.();
      }}
      className={DELETE_ROLE_BUTTON_CLASS_NAME}
      sx={{ "position": "relative", "&::after": { content: '""', position: "absolute", inset: "-16px" } }}
    >
      <TrashCanIcon color={(theme: Theme) => theme.palette.text.secondary} size={14} />
    </IconButton>
  );
};

const AddRoleButton: FC<{
  availableRoles: PartnerRole[];
  onAddRole: (role: PartnerRole) => Promise<unknown>;
}> = ({ availableRoles, onAddRole }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const addRoleMutation = useMutation({ mutationFn: onAddRole });

  return (
    <Box>
      <Button
        variant="text"
        onClick={e => {
          if (addRoleMutation.isPending) return;
          setAnchorEl(e.currentTarget);
        }}
        aria-disabled={addRoleMutation.isPending}
        sx={theme => ({
          "py": 1.5,
          "px": 2,
          "fontWeight": 600,
          "fontSize": theme.typography.pxToRem(12),
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
        })}
      >
        {t("Partner.Roles.AddRole")}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
      >
        {availableRoles.map(role => (
          <MenuItem
            key={role}
            onClick={() => {
              addRoleMutation.mutate(role);
              setAnchorEl(null);
            }}
          >
            <Stack direction="row" sx={{ gap: 1.5, alignItems: "center" }}>
              {ROLES_INFO[role].renderIcon({ size: "sm", activeStatus: "active" })}
              <Stack>
                <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600 }}>
                  {t(ROLES_INFO[role].labelKey)}
                </Typography>
              </Stack>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const RoleCardEmptyState = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: theme => theme.palette.grey["300"],
        borderRadius: 1,
        display: "grid",
        height: "172px",
        placeContent: "center",
      }}
    >
      <Stack sx={{ gap: 0.5, alignItems: "center", textAlign: "center" }}>
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(14),
            fontWeight: 600,
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {t("Partner.Roles.EmptyState.Title")}
        </Typography>
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            maxWidth: "30rem",
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {t("Partner.Roles.EmptyState.Body")}
        </Typography>
      </Stack>
    </Box>
  );
};
